import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { observer, inject } from 'mobx-react';
import { useParams, useHistory } from 'react-router-dom';
import * as routes from 'routes';
import { ACCOUNTS_COMPLETE } from 'routes';
import {
  API_ACCOUNT_COMPLETE,
  API_ACCOUNT_SIGNATURE,
  API_ACCOUNT_SIGNATURE_RESTART,
  API_ACCOUNT_SIGNATURE_DATA,
} from 'api/endpoints';

import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import GetAppIcon from '@material-ui/icons/GetApp';
import HelpIconOutline from '@material-ui/icons/HelpOutline';
import { CircularProgress } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles/createTheme';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Link from '@material-ui/core/Link';
import Container from '@material-ui/core/Container';

import Info from 'components/Tooltip/Info';
import { SingleValueResponse } from 'common/interfaces';
import { snakeCaseToUpperCase } from 'helpers/helpers';
import MaterialTable from 'components/Table/Table';
import Background from 'components/Layout/Background';
import ControlButtons from 'components/ControlButtons';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import SmsDialog from './SmsDialog';
import CompleteDialog from './CompleteDialog';
import { ContractTab } from '../Contract';
import { ApiResponseError } from 'stores/apiStore';
import { TemplateType } from 'apps/Pdf/templates/TemplateType';
import { Params } from 'pages/Accounts/Edit/accountEditRoutes';
import { RootStore } from 'stores/rootStore';
import { EditAccountStore } from 'stores/editAccountStore';
import { Permission } from 'helpers/permissions';

interface DownloadDocument {
  name: string;
  template?: TemplateType;
  link?: string;
  loading?: boolean;
  placeholderText?: string;
}

const _documents: DownloadDocument[] = [
  {
    name: 'pdf_document_MAIN_CONTRACT',
    template: 'mainContract',
  },
  {
    name: 'pdf_document_CONDITIONS',
    link: 'https://www.edwardinvest.cz/download/64/pravni-dokumenty/3310/obchodni-podminky.pdf',
  },
  {
    name: 'pdf_document_PRICE_LIST',
    template: 'priceList',
  },
  {
    name: 'pdf_document_INVESTMENT_QUESTIONNAIRE',
    template: 'investmentQuestionnaire',
  },
  {
    name: 'pdf_document_FINANCIAL_QUESTIONNAIRE',
    template: 'financialQuestionnaire',
  },
  {
    name: 'pdf_document_INVESTMENT_PLAN',
    template: 'investmentPlan',
  },
  {
    name: 'pdf_document_AML_QUESTIONNAIRE',
    template: 'amlQuestionnaire',
  },
];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    disabledButton: {
      "& svg[class*='hoverIcon']": {
        color: `${theme.palette.primary.main} !important`,
      },
    },
    controlButtons: {
      marginTop: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& > div > div': {
        marginTop: 0,
      },
      '& button': {
        marginLeft: theme.spacing(1),
      },
    },
  }),
);

interface Props {
  rootStore?: RootStore;
  tabChange?: (tab: ContractTab) => void;
  editAccountStore?: EditAccountStore;
  toBeSigned?: boolean;
}
interface SignatureData {
  phone: string;
  email: string;
  foreignResidency: boolean;
  resend: boolean;
  includeMeetingRecord: boolean;
  completedQuestionnaire: boolean;
  includeMainContract: boolean;
  includeInformationSheet: boolean;
  completionAllowed: boolean;
  includeLtipAgreement: boolean;
}

const ContractDocuments: React.FC<Props> = ({ tabChange, editAccountStore, toBeSigned, rootStore }) => {
  const { uiStore, apiStore, userStore } = rootStore!.stores;

  const [documents, setDocuments] = useState(_documents);
  const [isSmsDialogOpen, setIsSmsDialogOpen] = useState(false);
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
  const [isCompleteConfirmPending, setIsCompleteConfirmPending] = useState(false);
  const [completeDialogErrors, setCompleteDialogErrors] = useState<ApiResponseError[]>([]);

  const [isCancelSigningDialogOpen, setIsCancelSigningDialogOpen] = useState(false);
  const [isCancelSigningCompleteDialogOpen, setIsCancelSigningCompleteDialogOpen] = useState(false);
  const [cancelSigningCompleteText, setCancelSigningCompleteText] = useState('');

  const [isRestartSigningDialogOpen, setIsRestartSigningDialogOpen] = useState(false);
  const [isRestartSigningCompleteDialogOpen, setIsRestartSigningCompleteDialogOpen] = useState(false);
  const [restartSigningCompleteText, setRestartSigningCompleteText] = useState('');

  const { t } = useTranslation();
  const { accountId } = useParams<Params>();
  const history = useHistory();
  const { get, patch } = apiStore;
  const [signatureData, setSignatureData] = useState<SignatureData | undefined>(undefined);
  const styles = useStyles();

  useEffect(() => {
    (async () => {
      uiStore && uiStore.setLoading(true);
      const response = await get<SignatureData>(API_ACCOUNT_SIGNATURE_DATA(accountId));
      if (response?.data) {
        setSignatureData(response.data);
        const modifiedSet = JSON.parse(JSON.stringify(_documents));
        if (!response.data?.includeMainContract) {
          modifiedSet[0].placeholderText = 'pdf_document_MAIN_CONTRACT_placeholder';
        }
        if (response.data?.includeMeetingRecord === true || response.data?.includeMeetingRecord === undefined) {
          modifiedSet.splice(4, 0, {
            name: 'pdf_document_MEETING_RECORD',
            template: 'meetingRecord',
          });
        }
        if (response.data?.foreignResidency) {
          modifiedSet.splice(modifiedSet.length, 0, {
            name: 'pdf_document_INCOME_OWNERSHIP',
            template: 'incomeOwnership',
          });
        }
        if (response.data?.includeLtipAgreement) {
          modifiedSet.splice(modifiedSet.length, 0, {
            name: 'pdf_document_LTIP_CONDITIONS',
            link: 'https://www.edwardinvest.cz/download/64/pravni-dokumenty/5578/produktove-podminky-pro-duchodovy-ucet_edward_20240101.pdf',
          });
          modifiedSet.splice(modifiedSet.length, 0, {
            name: 'pdf_document_LTIP_AGREEMENT',
            template: 'ltipAgreement',
          });
        }
        if (response.data?.includeInformationSheet) {
          modifiedSet.splice(modifiedSet.length, 0, {
            name: 'pdf_document_INFORMATION_SHEET',
            template: 'informationSheet',
          });
        }


        setDocuments(modifiedSet);
      }
      uiStore && uiStore.setLoading(false);
    })();
  }, [accountId, get, uiStore]);

  const setLoadingForDocument = (document: DownloadDocument, loading: boolean) => {
    setDocuments(
      documents.map((doc) => {
        if (doc.template === document.template) {
          doc.loading = loading;
        }

        return doc;
      }),
    );
  };
  const downloadDocument = (document: DownloadDocument) => {
    if (document.template) {
      setLoadingForDocument(document, true);
      get(`/account/${accountId}/pdf/${snakeCaseToUpperCase(document.template)}`).then(() => {
        setLoadingForDocument(document, false);
      });
    }
  };

  const handleBack = () => {
    tabChange && tabChange(ContractTab.ADDITIONAL_INFO);
  };

  const handleNext = () => {
    setIsCompleteDialogOpen(true);
  };

  const handleCompleteDialogClose = () => {
    setIsCompleteDialogOpen(false);
  };

  const handleCompleteDialogConfirm = async () => {
    setIsCompleteConfirmPending(true);
    const response = await patch<SingleValueResponse<boolean>>(API_ACCOUNT_COMPLETE(accountId));

    setIsCompleteConfirmPending(false);

    if (response?.errors) {
      setCompleteDialogErrors(response.errors);
    }

    if (response?.data?.value === true) {
      history.push(ACCOUNTS_COMPLETE(accountId));
    }
  };

  const cancelSigning = async () => {
    uiStore.setLoading(true);
    const response = await apiStore.delete<SingleValueResponse<boolean>>(API_ACCOUNT_SIGNATURE(accountId));
    uiStore.setLoading(false);
    setIsCancelSigningDialogOpen(false);
    setIsCancelSigningCompleteDialogOpen(true);
    if (!response || response?.errors) {
      setCancelSigningCompleteText(t('contract_documents_cancelSigning_error'));
    } else if (response?.data?.value === true) {
      setCancelSigningCompleteText(t('contract_documents_cancelSigning_success'));
    } else {
      setCancelSigningCompleteText(t('contract_documents_cancelSigning_signed'));
    }
  };

  const restartSigning = async () => {
    uiStore.setLoading(true);
    const response = await apiStore.patch<SingleValueResponse<boolean>>(API_ACCOUNT_SIGNATURE_RESTART(accountId));
    uiStore.setLoading(false);
    setIsRestartSigningDialogOpen(false);
    setIsRestartSigningCompleteDialogOpen(true);
    if (!response || response?.errors) {
      setRestartSigningCompleteText(t('contract_documents_restartSignature_error'));
    } else if (response?.data?.value === true) {
      setRestartSigningCompleteText(t('contract_documents_restartSignature_success'));
    } else {
      setRestartSigningCompleteText(t('contract_documents_restartSignature_signed'));
    }
  };

  return (
    <>
      <SmsDialog open={isSmsDialogOpen} handleClose={() => setIsSmsDialogOpen(false)} />
      <CompleteDialog
        open={isCompleteDialogOpen}
        email={signatureData?.email}
        phone={signatureData?.phone}
        isConfirmPending={isCompleteConfirmPending}
        errors={completeDialogErrors}
        handleConfirm={() => handleCompleteDialogConfirm()}
        handleClose={() => handleCompleteDialogClose()}
        accountId={accountId}
      />

      <ConfirmDialog
        title="contract_documents_cancelSigning"
        btnConfirmText="contract_documents_cancelSigning_confirm"
        isOpen={isCancelSigningDialogOpen}
        close={() => setIsCancelSigningDialogOpen(false)}
        confirm={() => cancelSigning()}
      >
        {t('contract_documents_cancelSigning_info')}
      </ConfirmDialog>

      <ConfirmDialog
        title="contract_documents_cancelSigning"
        isOpen={isCancelSigningCompleteDialogOpen}
        close={() => setIsCancelSigningCompleteDialogOpen(false)}
        btnConfirmText="contract_documents_back"
        confirm={() => history.push(routes.ACCOUNTS_DRAFTS)}
      >
        {cancelSigningCompleteText}
      </ConfirmDialog>

      <ConfirmDialog
        title="contract_documents_restartSignature"
        btnConfirmText="contract_documents_restartSignature_confirm"
        isOpen={isRestartSigningDialogOpen}
        close={() => setIsRestartSigningDialogOpen(false)}
        confirm={() => restartSigning()}
      >
        {t('contract_documents_restartSignature_info')}
      </ConfirmDialog>

      <ConfirmDialog
        title="contract_documents_restartSignature"
        isOpen={isRestartSigningCompleteDialogOpen}
        close={() => setIsRestartSigningCompleteDialogOpen(false)}
        btnConfirmText="contract_documents_back"
        confirm={() => history.push(routes.ACCOUNTS_DRAFTS)}
      >
        {restartSigningCompleteText}
      </ConfirmDialog>

      <Container maxWidth="md">
        <Background afterMenu={!toBeSigned}>
          <Box marginBottom={4} marginTop={2}>
            <Typography variant="body2">
              {t(toBeSigned ? 'contract_documents_toBeSigned_info' : 'contract_documents_info')}
            </Typography>
            {!toBeSigned && (
              <Button variant="text" onClick={() => setIsSmsDialogOpen(true)} startIcon={<HelpIconOutline />}>
                {t('contract_documents_SMS_howDoesItWork')}
              </Button>
            )}
          </Box>
          <Typography variant="h2">
            {t(toBeSigned ? 'contract_documents_toBeSigned_title' : 'contract_documents_title')}
          </Typography>
          <MaterialTable
            options={{
              header: false,
              sorting: false,
              draggable: false,
              search: false,
              toolbar: false,
              paging: false,
            }}
            data={documents}
            columns={[
              { field: 'name', render: (document) => t(document.name) },
              {
                field: 'template',
                render: (document) =>
                  document.placeholderText ? (
                    <Info content={t(document.placeholderText)} />
                  ) : document.link ? (
                    <Link href={document.link} target="_blank" underline="none">
                      <Button variant="outlined" className="download-document-button" startIcon={<GetAppIcon />}>
                        {t('general_download')}
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      variant="outlined"
                      className="download-document-button"
                      startIcon={document.loading ? <CircularProgress size={20} /> : <GetAppIcon />}
                      onClick={() => downloadDocument(document)}
                    >
                      {t('general_download')}
                    </Button>
                  ),
                cellStyle: {
                  width: '10%',
                  textAlign: 'center',
                },
              },
            ]}
          />
        </Background>
      </Container>

      {toBeSigned ? (
        <div className={styles.controlButtons}>
          <ControlButtons
            isSubmitting={false}
            backText={t('contract_documents_back')}
            onBack={() => history.push(routes.ACCOUNTS_DRAFTS)}
            nextText={t('contract_documents_cancelSigning')}
            onNext={() => setIsCancelSigningDialogOpen(true)}
            lastStep
          />
          {signatureData?.resend && userStore.checkPermissions(Permission.ACCOUNT_REQUEST_SIGN) && (
            <Button
              variant="contained"
              color="primary"
              type="button"
              onClick={() => setIsRestartSigningDialogOpen(true)}
            >
              {t('contract_documents_restartSignature')}
            </Button>
          )}
        </div>
      ) : (
        <div
          className={
            ((!editAccountStore?.isDraft || !signatureData?.completedQuestionnaire) && styles.disabledButton) || ''
          }
        >
          <ControlButtons
            isSubmitting={false}
            backText={t('contract_additionalInfo')}
            onBack={() => handleBack()}
            nextText={t('contract_sendToClient')}
            onNext={() => handleNext()}
            nextDisabled={
              !editAccountStore?.isDraft || !signatureData?.completedQuestionnaire || !signatureData?.completionAllowed
            }
            nextTooltipContent={
              !signatureData?.completionAllowed
                ? t('contract_documents_waitingForSignature')
                : !signatureData?.completedQuestionnaire
                ? t('skippedQuestionnaire_info')
                : !editAccountStore?.isDraft
                ? t('accountMock_endInfo')
                : undefined
            }
          />
        </div>
      )}
    </>
  );
};

export default inject('apiStore', 'uiStore', 'editAccountStore', 'rootStore')(observer(ContractDocuments));
